require('flickity-fullscreen');
import React, { useRef, useState, useEffect } from 'react';
import Flickity from 'react-flickity-component';
import { isEmpty, map } from 'lodash';
import Globalizer from '../../../globalizer';
import common from '../../../common/common';
import translateService from '../../../common/translateService';
import {
    toggleNextElementVisibility,
    handleAbuseTypeChange,
} from '../../../services/product/productInteraction';
import ProductFullScreenHandler from '../products/ProductFullScreenHandler.jsx';
import { reportQuestionAbuse } from '../../../services/questionAndAnswers/questionAndAnswersService';
import ProductAnswer from './ProductAnswer.jsx';
import deferredImagesLoader from '../../../deferredImagesLoader';

const ProductQuestionAndAnswer = ({ isMobile, isAuthenticated, question }) => {
    Globalizer.init();
    const questionRef = useRef(null);
    const flktyHtmlRef = useRef(null);
    const reportAbuseFormRef = useRef(null);
    const [questionState, setQuestionState] = useState(question);
    const [flktyRef, setFlktyRef] = useState();
    const [abuseType, setAbuseType] = useState('');
    const [fullScreenActive, setFullScreenActive] = useState(false);
    const [indexZoom, setIndexZoom] = useState(0);

    useEffect(() => {
        setQuestionState(question);
        deferredImagesLoader.observeReviewGallery({
            element: questionRef.current,
            cb: (imageToLazyLoad) => {
                if (imageToLazyLoad) {
                    setQuestionState({ ...question, renderImages: true });
                }
            },
        });
    }, [question]);

    const handleRadioButtonClick = (e) => {
        handleAbuseTypeChange(e, reportAbuseFormRef);
        const type = e.currentTarget.value;
        setAbuseType(type);
    };

    async function handleSendReportAbuse() {
        const result = await reportQuestionAbuse(
            question.id,
            question.productSlug,
            abuseType
        );

        if (result.isSuccess) {
            reportAbuseFormRef.current.classList.remove('open');
        }
    }

    const handleImageClick = (e, index) => {
        setFullScreenActive(!fullScreenActive);
        setIndexZoom(index);
    };

    const images = map(question.images, (image, idx) => (
        <div className='questions-cell' key={idx}>
            <img src={image} onClick={(e) => handleImageClick(e, idx)} />
        </div>
    ));

    const answers = map(question.answers, (a, idx) => (
        <ProductAnswer
            answer={a}
            key={idx}
            isAuthenticated={isAuthenticated}
            slug={question.productSlug}
            questionId={question.id}
        />
    ));

    if (flktyRef) {
        flktyRef.on('fullscreenChange', (isFullscreen) => {
            const body = document.querySelector('body');

            if (isFullscreen) {
                body.classList.add('no-scroll');
            } else {
                body.classList.remove('no-scroll');
            }
        });
    }

    return (
        <div className='prd-question' ref={questionRef}>
            <div className='prd-question-heading'>
                <p>
                    {`${question.user.nickname} | ${Globalizer.formatDateLong(
                        question.creationDate
                    )}`}
                </p>
            </div>
            <div className='prd-question-content'>
                <p dangerouslySetInnerHTML={{ __html: question.question }} />
                {!isEmpty(question.images) &&
                    questionState.renderImages === true && (
                        <Flickity
                            className='qanda'
                            options={{
                                autoPlay: false,
                                cellAlign: 'left',
                                contain: true,
                                draggable: isMobile,
                                fullscreen: false,
                                groupCells: '100%',
                                pageDots: true,
                                prevNextButtons: false,
                                wrapAround: false,
                            }}
                            ref={flktyHtmlRef}
                            flickityRef={(c) => {
                                setFlktyRef(c);
                            }}>
                            {images}
                        </Flickity>
                    )}
                {fullScreenActive && (
                    <ProductFullScreenHandler
                        options={{
                            infinite: false,
                            on: {
                                closing: (fancybox, slide) => {},
                            },
                            startIndex: indexZoom,
                        }}
                        items={question.images.map((i) => ({
                            src: i,
                            type: 'image',
                        }))}></ProductFullScreenHandler>
                )}
            </div>

            <div className='prd-question-footer'>
                {isAuthenticated && (
                    <div className='prd-question-reportAbuse'>
                        <p
                            className='prd-link'
                            onClick={toggleNextElementVisibility}>
                            {translateService.get(
                                `questions.reportQuestionAbuse`
                            )}
                        </p>
                        <div
                            className='prd-question-reportAbuse-form'
                            ref={reportAbuseFormRef}>
                            <p>
                                {translateService.get(
                                    `questions.questionAbuseKind`
                                )}
                            </p>
                            <label htmlFor='vulgar'>
                                <input
                                    onMouseUp={handleRadioButtonClick}
                                    type='radio'
                                    name='vulgar'
                                    value='Vulgar'
                                />
                                {translateService.get(`productDetail.vulgar`)}
                            </label>
                            <label htmlFor='spam'>
                                <input
                                    onMouseUp={handleRadioButtonClick}
                                    type='radio'
                                    name='spam'
                                    value='Spam'
                                />
                                {translateService.get(`productDetail.spam`)}
                            </label>
                            <label htmlFor='useless'>
                                <input
                                    onMouseUp={handleRadioButtonClick}
                                    type='radio'
                                    name='useless'
                                    value='Useless'
                                />
                                {translateService.get(`productDetail.useless`)}
                            </label>
                            <p>
                                <a
                                    className='prd-link'
                                    onClick={handleSendReportAbuse}>
                                    {translateService.get(`productDetail.send`)}
                                </a>
                            </p>
                        </div>
                    </div>
                )}
                <div className='prd-question-answerLink'>
                    <a
                        className='prd-link'
                        href={common.getSiteUrl(question.answerQuestionLink)}>
                        {translateService.get(`questions.answerQuestionLink`)}
                    </a>
                </div>
            </div>

            {isEmpty(question.answers) ? (
                <div className='prd-question-answers-container'>
                    <p className='prd-question-answers-number'>
                        {translateService.get(`questions.noAnswers`)}
                    </p>
                </div>
            ) : (
                <div className='prd-question-answers-container'>
                    <p
                        className='prd-question-answers-number showAnswers prd-link'
                        onClick={toggleNextElementVisibility}>
                        {question.answers.length === 1
                            ? translateService.get(`questions.oneAnswer`)
                            : translateService.interpolate(
                                  `questions.answers`,
                                  { x: question.answers.length }
                              )}
                    </p>
                    <div className='prd-question-answers'>{answers}</div>
                </div>
            )}
        </div>
    );
};

export default ProductQuestionAndAnswer;
